import { FormEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { DateTimeField } from "../../components/DateTimeField";
import { EmptyState } from "../../components/EmptyState";
import * as Fonts from "../../components/Fonts";
import Input from "../../components/Form/Input/input";
import { Select } from "../../components/Form/Input/select";
import Label from "../../components/Form/Label";
import { FormGroup } from "../../components/Form/styles";
import { KeepoLoader } from "../../components/KeepoLoader";
import Modal, { ModalButton, ModalFooterLink, ModalSecondaryButton } from "../../components/Modal";
import { AGENDA_ERRORS, errorCodeOf, refundBooking } from "../../services/agenda";
import { formatDateTime } from "../../services/i18ln/format";
import { getHistory, PaymentHistoryFilters, PaymentHistoryItem } from "../../services/payments";
import { formatCents } from "../Agenda/format";
import { todayIn } from "../Agenda/timezone";
import { SmallButton } from "../Domains/styles";
import { Card, Section } from "../Settings/styles";
import { chargesPrefix } from "./state";

const prefix = `${chargesPrefix}.history`;

/** The type filter: the payment methods, the refunds and the withdrawals */
const TYPES = ["pix", "card", "boleto", "refund", "withdrawal"] as const;
/** The status filter, as the items show them */
const STATUSES = ["paid", "pending", "processing", "canceled", "expired", "failed", "refunded", "chargeback", "review"];
const PERIODS = ["all", "7d", "30d", "month", "lastMonth", "custom"] as const;
type Period = (typeof PERIODS)[number];

/** The item's status as the owner reads it: a withdrawal still pending is "processing" for them. */
const statusOf = (item: PaymentHistoryItem) =>
  item.type === "withdrawal" && item.status === "pending" ? "processing" : item.status;

const STATUS_COLORS: Record<string, string> = {
  paid: "var(--color-feedback-success)",
  pending: "#ffd00d",
  processing: "#ffd00d",
  review: "#ffb066",
  canceled: "var(--color-neutral-150)",
  expired: "var(--color-neutral-150)",
  failed: "#ff8aa0",
  refunded: "var(--color-neutral-150)",
  chargeback: "#ff8aa0",
};

/** The timezone the API reads the history's days in (the lines' dates are written in it too, so they match the filters) */
const HISTORY_TIMEZONE = "America/Sao_Paulo";

/** A line's date: Brasília's time, with the year when it isn't this one */
const dateOf = (at: string, language: string) =>
  formatDateTime(at, language, { timeZone: HISTORY_TIMEZONE, yearIfNotCurrent: true });

/** YYYY-MM-DD of a calendar day (kept in UTC, so the device's timezone never shifts it) */
const day = (date: Date) => date.toISOString().slice(0, 10);

/** The days a period shortcut covers, both included (today's in Brasília's time, as the API reads them) */
const periodDays = (period: Period, todayText = todayIn(HISTORY_TIMEZONE)): { from?: string; to?: string } => {
  const [year, month, date] = todayText.split("-").map(Number);
  const today = new Date(Date.UTC(year, month - 1, date));
  const back = (days: number) => new Date(Date.UTC(year, month - 1, date - days));
  switch (period) {
    case "7d":
      return { from: day(back(6)), to: day(today) };
    case "30d":
      return { from: day(back(29)), to: day(today) };
    case "month":
      return { from: day(new Date(Date.UTC(year, month - 1, 1))), to: day(today) };
    case "lastMonth":
      return {
        from: day(new Date(Date.UTC(year, month - 2, 1))),
        to: day(new Date(Date.UTC(year, month - 1, 0))),
      };
    default:
      return {};
  }
};

type Props = {
  siteId: string;
  /** Changes when something new happened (a withdrawal): the history starts over from the newest. */
  reloadKey: number;
  /** A sale was refunded here */
  onChanged?: () => void;
};

/**
 * Últimos lançamentos: the Keepo's sales (every status), refunds and the registration's withdrawals, the newest first,
 * one line each in a single box, searched by customer or service and filtered by type, status and period. A line opens
 * its detail. The next page loads as the end of the list shows (a button only where the browser can't tell).
 */
export const History = ({ siteId, reloadKey, onChanged }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [period, setPeriod] = useState<Period>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // the last day before the first: not searched, and said under the dates
  const [rangeError, setRangeError] = useState(false);
  const [items, setItems] = useState<PaymentHistoryItem[]>();
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setError] = useState(false);
  const [open, setOpen] = useState<PaymentHistoryItem>();
  // The Keepo on screen now: a page for another one (switched meanwhile) is dropped
  const current = useRef(siteId);
  current.current = siteId;
  // Each start over of the history: a next page asked before it is dropped
  const generation = useRef(0);
  // Bumped to read the history again (after a refund, on Tentar de novo), through the effect that drops older answers
  const [restart, setRestart] = useState(0);
  const reload = () => setRestart(key => key + 1);

  /** The search and filters as they are on screen now */
  const draft = (): PaymentHistoryFilters => {
    const days = period === "custom" ? { from: from || undefined, to: to || undefined } : periodDays(period);
    return { q: search, types: type ? [type] : [], statuses: status ? [status] : [], ...days };
  };
  // What the list shows: applied only on Buscar (or Enter), not while the owner is still choosing
  const [filters, setFilters] = useState<PaymentHistoryFilters>({});
  const filtered = !!(
    filters.q?.trim() ||
    filters.types?.length ||
    filters.statuses?.length ||
    filters.from ||
    filters.to
  );

  const apply = (event?: FormEvent) => {
    event?.preventDefault();
    // YYYY-MM-DD: the text order is the date order
    if (period === "custom" && from && to && from > to) {
      setRangeError(true);
      return;
    }
    setRangeError(false);
    setFilters(draft());
  };

  const clear = () => {
    setSearch("");
    setType("");
    setStatus("");
    setPeriod("all");
    setFrom("");
    setTo("");
    setRangeError(false);
    setFilters({});
  };

  const load = useCallback(() => {
    let isCurrent = true;
    generation.current += 1;
    setLoadingMore(false);
    setItems(undefined);
    setNextBefore(null);
    setError(false);
    getHistory(siteId, null, filters)
      .then(page => {
        if (!isCurrent) return;
        setItems(page.items);
        setNextBefore(page.nextBefore);
      })
      .catch(() => isCurrent && setError(true));
    return () => {
      isCurrent = false;
    };
  }, [siteId, filters]);

  useEffect(load, [load, reloadKey, restart]);

  const loadMore = useCallback(() => {
    if (!nextBefore) return;
    const site = siteId;
    const asked = generation.current;
    const isCurrent = () => current.current === site && generation.current === asked;
    setLoadingMore(true);
    setError(false);
    getHistory(site, nextBefore, filters)
      .then(page => {
        if (!isCurrent()) return;
        setItems(shown => [...(shown ?? []), ...page.items.filter(item => !shown?.some(old => sameItem(old, item)))]);
        setNextBefore(page.nextBefore);
      })
      .catch(() => isCurrent() && setError(true))
      .finally(() => isCurrent() && setLoadingMore(false));
  }, [siteId, nextBefore, filters]);

  const hasMore = !!items && !!nextBefore && !hasError;
  const canWatchEnd = typeof window.IntersectionObserver === "function";
  const listEnd = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const end = listEnd.current;
    if (!hasMore || loadingMore || !canWatchEnd || !end) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          observer.disconnect();
          loadMore();
        }
      },
      { rootMargin: "300px" }
    );
    observer.observe(end);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, canWatchEnd, loadMore]);

  return (
    <Section>
      <Fonts.Label>{t(`${prefix}.section`)}</Fonts.Label>
      <Card>
        <Filters role="search" onSubmit={apply}>
          <FormGroup className="search">
            <Input
              id="history-search"
              type="text"
              value={search}
              onChange={event => setSearch(event.target.value)}
              label={t(`${prefix}.search`)}
              placeholder={t(`${prefix}.searchPlaceholder`)}
              ignoreLengthInfo
            />
          </FormGroup>
          <FormGroup>
            <Select id="history-type" value={type} onChange={event => setType(event.target.value)}>
              <option value="">{t(`${prefix}.types.all`)}</option>
              {TYPES.map(value => (
                <option key={value} value={value}>
                  {t(`${prefix}.types.${value}`)}
                </option>
              ))}
            </Select>
            <Label htmlFor="history-type" text={t(`${prefix}.type`)} ignoreLengthInfo />
          </FormGroup>
          <FormGroup>
            <Select id="history-status" value={status} onChange={event => setStatus(event.target.value)}>
              <option value="">{t(`${prefix}.statuses.all`)}</option>
              {STATUSES.map(value => (
                <option key={value} value={value}>
                  {t(`${prefix}.statuses.${value}`)}
                </option>
              ))}
            </Select>
            <Label htmlFor="history-status" text={t(`${prefix}.status`)} ignoreLengthInfo />
          </FormGroup>
          <FormGroup>
            <Select
              id="history-period"
              value={period}
              onChange={event => {
                setPeriod(event.target.value as Period);
                setRangeError(false);
              }}>
              {PERIODS.map(value => (
                <option key={value} value={value}>
                  {t(`${prefix}.periods.${value}`)}
                </option>
              ))}
            </Select>
            <Label htmlFor="history-period" text={t(`${prefix}.period`)} ignoreLengthInfo />
          </FormGroup>
          {period === "custom" && (
            <>
              <FormGroup className="date">
                <DateTimeField
                  id="history-from"
                  kind="DATE"
                  label={t(`${prefix}.from`)}
                  placeholder={t(`${prefix}.datePlaceholder`)}
                  value={from}
                  onChange={value => {
                    setFrom(value);
                    setRangeError(false);
                  }}
                />
                <Label htmlFor="history-from" text={t(`${prefix}.from`)} ignoreLengthInfo />
              </FormGroup>
              <FormGroup className="date">
                <DateTimeField
                  id="history-to"
                  kind="DATE"
                  label={t(`${prefix}.to`)}
                  placeholder={t(`${prefix}.datePlaceholder`)}
                  value={to}
                  onChange={value => {
                    setTo(value);
                    setRangeError(false);
                  }}
                />
                <Label htmlFor="history-to" text={t(`${prefix}.to`)} ignoreLengthInfo />
              </FormGroup>
              {rangeError && <RangeError role="alert">{t(`${prefix}.rangeError`)}</RangeError>}
            </>
          )}
          <Actions>
            {filtered && (
              <button type="button" className="clear" onClick={clear}>
                {t(`${prefix}.clearFilters`)}
              </button>
            )}
            <SmallButton type="submit" $primary>
              {t(`${prefix}.searchButton`)}
            </SmallButton>
          </Actions>
        </Filters>
      </Card>

      {!items && !hasError && (
        <Card role="status" aria-label={t(`${prefix}.loading`)}>
          <Center>
            <KeepoLoader size={24} color="var(--color-neutral-white)" />
          </Center>
        </Card>
      )}
      {items &&
        items.length === 0 &&
        !hasError &&
        // with filters, one quiet row under them; with none, nothing sold yet
        (filtered ? (
          <EmptyState compact emoji="🔍" title={t(`${prefix}.noMatch`)} />
        ) : (
          <EmptyState emoji="💸" title={t(`${prefix}.emptyTitle`)} description={t(`${prefix}.emptyText`)} />
        ))}
      {items && items.length > 0 && (
        <Card>
          <List aria-label={t(`${prefix}.section`)}>
            {items.map(item => (
              <li key={`${item.type}-${item.id}`}>
                <Line item={item} onOpen={() => setOpen(item)} />
              </li>
            ))}
          </List>
        </Card>
      )}
      {hasError && (
        <Note role="alert">
          {t(`${prefix}.loadError`)}{" "}
          <button type="button" onClick={items ? loadMore : reload}>
            {t(`${prefix}.retry`)}
          </button>
        </Note>
      )}
      <div ref={listEnd} />
      {loadingMore && (
        <Center role="status" aria-label={t(`${prefix}.loadingMore`)}>
          <KeepoLoader size={20} color="var(--color-neutral-white)" />
        </Center>
      )}
      {hasMore && !canWatchEnd && !loadingMore && (
        <Note className="center">
          <button type="button" onClick={loadMore}>
            {t(`${prefix}.loadMore`)}
          </button>
        </Note>
      )}
      {open && (
        <Detail
          siteId={siteId}
          item={open}
          onClose={() => setOpen(undefined)}
          onChanged={() => {
            setOpen(undefined);
            onChanged?.();
            reload();
          }}
        />
      )}
    </Section>
  );
};

const sameItem = (a: PaymentHistoryItem, b: PaymentHistoryItem) => a.type === b.type && a.id === b.id;

// The left icons, in the same strokes as the Keepo's other ones
const Svg = ({ children }: { children: ReactNode }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {children}
  </svg>
);

const ICONS: Record<string, ReactNode> = {
  // Pix: its diamond
  pix: (
    <Svg>
      <path d="M12 3 21 12 12 21 3 12z" />
      <path d="M8 12h8M12 8v8" />
    </Svg>
  ),
  card: (
    <Svg>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </Svg>
  ),
  // boleto: a barcode
  boleto: (
    <Svg>
      <path d="M3 5v14M7 5v14M10 5v14M14 5v14M17 5v14M21 5v14" />
    </Svg>
  ),
  // money out to the bank
  withdrawal: (
    <Svg>
      <path d="M7 17 17 7M9 7h8v8" />
    </Svg>
  ),
  // money back to the customer
  refund: (
    <Svg>
      <path d="M17 7 7 17M15 17H7V9" />
    </Svg>
  ),
  // a sale whose method isn't chosen yet
  sale: (
    <Svg>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </Svg>
  ),
};

const iconOf = (item: PaymentHistoryItem) =>
  item.type === "sale" ? (ICONS[item.method ?? "sale"] ?? ICONS.sale) : ICONS[item.type];

/** What the line is: the customer and the service (a sale, a refund), or the withdrawal */
const titleOf = (item: PaymentHistoryItem, t: (key: string) => string) => {
  const who = [item.customer, item.service].filter(Boolean).join(" · ");
  if (item.type === "withdrawal") return t(`${prefix}.withdrawal`);
  if (item.type === "refund") return who ? `${t(`${prefix}.refund`)} · ${who}` : t(`${prefix}.refund`);
  return who || t(`${prefix}.sale`);
};

/** The amount of the line: what the owner got (+), gave back or took out (−); an unpaid sale, its price, plain */
const amountOf = (item: PaymentHistoryItem): { text: string; tone: "in" | "out" | "none" | "struck" } => {
  const money = (cents: number) => formatCents(Math.abs(cents), "BRL");
  if (item.type === "withdrawal") return { text: `−${money(item.paidCents ?? 0)}`, tone: "out" };
  if (item.type === "refund") return { text: `−${money(item.netCents)}`, tone: "out" };
  // a disputed sale's money was taken back: not an entry (no "+", not green), its value struck through
  if (item.status === "chargeback") return { text: money(item.netCents), tone: "struck" };
  if (["paid", "refunded"].includes(item.status)) return { text: `+${money(item.netCents)}`, tone: "in" };
  // a sale given up before it had a price shows none
  return { text: item.paidCents ? money(item.paidCents) : "", tone: "none" };
};

/** One line: the icon, what and when (and how), the amount and the status */
const Line = ({ item, onOpen }: { item: PaymentHistoryItem; onOpen: () => void }) => {
  const { t, i18n } = useTranslation();
  const status = statusOf(item);
  const amount = amountOf(item);
  const how = item.method ? t(`${prefix}.types.${item.method}`) : null;

  return (
    <Row type="button" onClick={onOpen}>
      <span className="icon" aria-hidden="true">
        {iconOf(item)}
      </span>
      <span className="text">
        <span className="title">{titleOf(item, t)}</span>
        <span className="description">{[dateOf(item.at, i18n.language), how].filter(Boolean).join(" · ")}</span>
      </span>
      <span className="end">
        {amount.text && <span className={`amount ${amount.tone}`}>{amount.text}</span>}
        <Status $color={STATUS_COLORS[status] ?? "var(--color-neutral-150)"}>
          {t(`${prefix}.statuses.${status}`)}
        </Status>
      </span>
    </Row>
  );
};

/** The line's detail: what, when and how, and its amounts (what was paid, the fees, what the owner got) */
type DetailProps = {
  siteId: string;
  item: PaymentHistoryItem;
  onClose: () => void;
  /** Refunded here (or found refunded already): the list and the balance are read again */
  onChanged: () => void;
};

/**
 * The line's detail: what, when and how, and its amounts (what was paid, the fees, what the owner got). A paid sale is
 * refunded from here, after asking (through its booking, as in the Agenda: the booking is canceled and its time freed).
 */
const Detail = ({ siteId, item, onClose, onChanged }: DetailProps) => {
  const { t, i18n } = useTranslation();
  const money = (cents: number) => formatCents(cents, "BRL");
  const status = statusOf(item);
  const [asking, setAsking] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [error, setError] = useState<string>();
  const canRefund = item.type === "sale" && item.status === "paid" && !!item.bookingId;

  const refund = async () => {
    if (!item.bookingId) return;
    setRefunding(true);
    setError(undefined);
    try {
      await refundBooking(siteId, item.bookingId);
      onChanged();
    } catch (failure) {
      const code = errorCodeOf(failure);
      // refunded already (or no longer paid): what's on screen is old
      if (code === AGENDA_ERRORS.notRefundable) {
        onChanged();
        return;
      }
      setError(
        t(`pages.agenda.bookings.refundErrors.${code === AGENDA_ERRORS.insufficientBalance ? "balance" : "other"}`)
      );
      setRefunding(false);
    }
  };

  const lines: [string, string][] =
    item.type === "sale"
      ? [
          [t(`${prefix}.detail.paid`), money(item.paidCents ?? 0)],
          [t(`${prefix}.detail.fees`), `−${money(item.feesCents ?? 0)}`],
          [t(`${prefix}.detail.net`), money(item.netCents)],
        ]
      : item.type === "refund"
        ? [[t(`${prefix}.detail.refunded`), `−${money(Math.abs(item.netCents))}`]]
        : [
            [t(`${prefix}.detail.amount`), money(item.paidCents ?? 0)],
            [t(`${prefix}.detail.fee`), `−${money(item.feesCents ?? 0)}`],
            [t(`${prefix}.detail.received`), money(item.netCents)],
          ];
  const facts: [string, string | null][] = [
    [t(`${prefix}.detail.customer`), item.customer ?? null],
    [t(`${prefix}.detail.service`), item.service ?? null],
  ];
  // when and how, under the title
  const subtitle = [dateOf(item.at, i18n.language), item.method ? t(`${prefix}.types.${item.method}`) : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal
      title={asking ? t("pages.agenda.bookings.refundTitle") : t(`${prefix}.${item.type}`)}
      subtitle={asking ? undefined : subtitle}
      size="md"
      outsideExit={!refunding}
      escapeExit
      busy={refunding}
      onExit={onClose}
      error={error}
      footer={
        asking ? (
          <>
            <ModalSecondaryButton type="button" disabled={refunding} onClick={() => setAsking(false)}>
              {t("pages.agenda.bookings.notNow")}
            </ModalSecondaryButton>
            <ModalButton loading={refunding} onClick={refund}>
              {t("pages.agenda.bookings.refund")}
            </ModalButton>
          </>
        ) : (
          <>
            {/* a quiet link on the left: refunding is there when needed, not what the detail is for */}
            {canRefund && (
              <ModalFooterLink type="button" onClick={() => setAsking(true)}>
                {t("pages.agenda.bookings.refund")}
              </ModalFooterLink>
            )}
            <ModalSecondaryButton type="button" onClick={onClose}>
              {t(`${prefix}.detail.close`)}
            </ModalSecondaryButton>
          </>
        )
      }>
      {asking ? (
        <Question>{t("pages.agenda.bookings.refundText", { amount: money(item.paidCents ?? 0) })}</Question>
      ) : (
        <DetailBody>
          <Status $color={STATUS_COLORS[status] ?? "var(--color-neutral-150)"}>
            {t(`${prefix}.statuses.${status}`)}
          </Status>
          {facts.some(fact => fact[1]) && (
            <dl>
              {facts
                .filter((fact): fact is [string, string] => !!fact[1])
                .map(([label, value]) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
            </dl>
          )}
          {facts.some(fact => fact[1]) && <div className="divider" />}
          <dl>
            {lines.map(([label, value], index) => (
              <div key={label} className={index === lines.length - 1 && lines.length > 1 ? "total" : undefined}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </DetailBody>
      )}
    </Modal>
  );
};

/* The search over the three filters (one under the other on a phone), and Buscar under them */
const Filters = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
  gap: 0.75rem;
  align-items: end;

  /* the fields' own spacing is the grid's gap here */
  > * {
    margin: 0;
  }

  .search {
    grid-column: 1 / -1;
  }

  /* the date field has none of the Input's and Select's 8px around the box: the same space from its label */
  .date > :first-child {
    margin: 0.5rem 0;
  }
`;

/* Buscar on the right, and "Limpar filtros" before it while something is applied */
const Actions = styled.div`
  display: flex;
  grid-column: 1 / -1;
  gap: 1rem;
  align-items: center;
  justify-content: flex-end;

  .clear {
    font-family: Inter, sans-serif;
    font-size: 0.875rem;
    color: var(--color-neutral-150);
    text-decoration: underline;
    transition: color 0.15s;

    &:hover {
      color: var(--color-neutral-white);
    }
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0;
`;

const Note = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-neutral-150);

  &.center {
    text-align: center;
  }

  button {
    color: var(--color-neutral-white);
    text-decoration: underline;
  }
`;

/* The lines, in one box, a divider between them */
const List = styled.ul`
  display: flex;
  flex-direction: column;

  li + li {
    border-top: 1px solid var(--color-neutral-base);
  }
`;

/* One line: icon, text, and the amount over the status on the right */
const Row = styled.button`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  padding: 0.75rem 0;
  font-family: Inter, sans-serif;
  text-align: left;

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
    border-radius: 10px;
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .title {
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .description {
    font-size: 0.75rem;
    line-height: 1rem;
    color: var(--color-neutral-150);
  }

  .end {
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    gap: 0.125rem;
    align-items: flex-end;
  }

  .amount {
    font-size: 0.875rem;
    font-weight: 700;
    line-height: 1.25rem;
    color: var(--color-neutral-white);
  }

  .amount.none {
    color: var(--color-neutral-150);
  }

  .amount.struck {
    color: var(--color-neutral-150);
    text-decoration: line-through;
  }

  &:hover .title {
    text-decoration: underline;
  }
`;

/* Under the dates: the last day is before the first */
const RangeError = styled.p`
  grid-column: 1 / -1;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1rem;
  color: var(--color-feedback-danger);
`;

/* A status with its ● colored dot, like a domain's on /domains */
const Status = styled.span<{ $color: string }>`
  display: inline-flex;
  gap: 0.375rem;
  align-items: center;
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1rem;
  color: ${({ $color }) => $color};

  &::before {
    width: 6px;
    height: 6px;
    content: "";
    background-color: currentColor;
    border-radius: 100%;
  }
`;

/* The detail: its facts, then its amounts, the last one (what the owner got) stronger */
const DetailBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  font-family: Inter, sans-serif;

  dl {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  dl > div {
    display: flex;
    gap: 1rem;
    justify-content: space-between;
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  dt {
    color: var(--color-neutral-150);
  }

  dd {
    color: var(--color-neutral-white);
    text-align: right;
  }

  .total dt,
  .total dd {
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .divider {
    height: 1px;
    background: var(--color-neutral-base);
  }
`;

/* The refund question, in the modal's body */
const Question = styled.p`
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: var(--color-neutral-150);
`;
